import { useEffect, useRef } from 'react';
import {
  useCreateConfigurationSettingsMutation,
  useDeployConfigurationSettingsMutation,
  useUpdateConfigurationSettingsPromptMutation,
} from 'src/services';
import { useNotification } from 'src/hooks/useNotification';
import type { ConfigurationSettings } from 'src/services/configurationService';

// Owns the create/update/deploy mutations for configuration settings and
// their result side effects (notifications, resetting the in-flight guard).
// Callers are notified of the outcomes via callbacks rather than this hook
// reaching into page state directly.
export const useConfigurationSettingsMutations = ({
  onCreated,
  onUpdated,
  onDeployed,
}: {
  onCreated: (data: ConfigurationSettings) => void;
  onUpdated: () => void;
  onDeployed: () => void;
}) => {
  const notification = useNotification();
  const isSubmitting = useRef(false);

  const [
    createConfigurationSettings,
    {
      data: createConfigurationSettingsData,
      isLoading: isLoadingCreateConfigurationSettings,
      isSuccess: isSuccessCreateConfigurationSettings,
      isError: isErrorCreateConfigurationSettings,
    },
  ] = useCreateConfigurationSettingsMutation();

  const [
    updateConfigurationSettings,
    {
      isLoading: isLoadingUpdateConfigurationSettings,
      isSuccess: isSuccessUpdateConfigurationSettings,
      isError: isErrorUpdateConfigurationSettings,
    },
  ] = useUpdateConfigurationSettingsPromptMutation();

  const [
    deployConfigurationSettings,
    {
      isLoading: isLoadingDeployConfigurationSettings,
      isSuccess: isSuccessDeployConfigurationSettings,
    },
  ] = useDeployConfigurationSettingsMutation();

  useEffect(() => {
    if (isErrorCreateConfigurationSettings || isErrorUpdateConfigurationSettings) {
      isSubmitting.current = false;
    }
  }, [isErrorCreateConfigurationSettings, isErrorUpdateConfigurationSettings]);

  useEffect(() => {
    if (isSuccessUpdateConfigurationSettings) {
      notification.success(`Configuration settings updated.`);
      isSubmitting.current = false;
      onUpdated();
    }
  }, [isSuccessUpdateConfigurationSettings]);

  useEffect(() => {
    if (isSuccessDeployConfigurationSettings) {
      notification.success(`Configuration settings deployed.`);
      onDeployed();
    }
  }, [isSuccessDeployConfigurationSettings]);

  useEffect(() => {
    if (isSuccessCreateConfigurationSettings) {
      notification.success(
        `Configuration settings v${createConfigurationSettingsData.version} created.`,
      );
      isSubmitting.current = false;
      onCreated(createConfigurationSettingsData);
    }
  }, [isSuccessCreateConfigurationSettings]);

  return {
    createConfigurationSettings,
    updateConfigurationSettings,
    deployConfigurationSettings,
    isSubmitting,
    isLoadingCreateConfigurationSettings,
    isLoadingUpdateConfigurationSettings,
    isLoadingDeployConfigurationSettings,
  };
};
